import { round2 } from "./wholesale";
import { priceItem, resolveDiscountPct, orderTotals, vatBreakdown } from "./wholesale-pricing";

/** Stavka kakvu drži uređivač u pregledniku: uz podatke za slanje nosi i prikazna polja
 *  (naziv, SKU, slika) da tabela ne mora ponovo u katalog nakon svake izmjene. */
export type EditorItem = {
  storeId: string;
  storeName: string;
  wcProductId: number;
  wcVariationId: number | null;
  sku: string;
  name: string;
  imageUrl: string;
  quantity: number;
  listPrice: number;
  discountPct: number;
  /** `null` = cijena po rabatu, broj = ručno upisana cijena. */
  overridePrice: number | null;
  unavailable?: boolean;
  stockQuantity?: number | null;
};

/** Ono što ide na `PUT /orders/:id/items`. */
export type EditorItemInput = {
  storeId: string;
  wcProductId: number;
  wcVariationId: number | null;
  quantity: number;
  overridePrice: number | null;
};

/** Stavka je jednoznačna po shopu i proizvodu/varijaciji — isti ključ koristi i server. */
export function editorItemKey(i: { storeId: string; wcProductId: number; wcVariationId: number | null }): string {
  return `${i.storeId}:${i.wcProductId}:${i.wcVariationId ?? 0}`;
}

/** Rabat za stavku: rabat po shopu ako partner ima, inače globalni rabat partnera. */
export function discountForStore(
  storeId: string,
  partnerDiscount: number,
  storeDiscounts?: { storeId: string; discountPct: number }[],
): number {
  const hit = (storeDiscounts ?? []).find((d) => d.storeId === storeId);
  return resolveDiscountPct({ storeDiscountPct: hit ? hit.discountPct : null, partnerDiscountPct: partnerDiscount });
}

export function unitPriceOf(item: Pick<EditorItem, "listPrice" | "discountPct" | "overridePrice">): number {
  // Ista formula kao na serveru — `priceItem` je jedini vlasnik računa rabata, da se prikaz u
  // uređivaču ne razidje s cijenom koja se upisuje. Količina ovdje nije bitna (samo unitPrice).
  //
  // `priceItem` baca na nevaljao unos, a ovo se zove iz rendera (nema error boundaryja): jedan
  // minus otkucan u polju cijene srušio bi uređivač i pojeo nespremljeni nacrt. Zato se negativan
  // override ovdje svede na 0 — tačno ono što se i sprema, jer je i sam unos klampan na ≥ 0 — a
  // svaki drugi neočekivani unos daje NaN, koji `fmtKm` ispiše kao crticu umjesto da ruši prikaz.
  const override = item.overridePrice === null || item.overridePrice === undefined ? null : Math.max(0, item.overridePrice);
  try {
    return priceItem({ listPrice: item.listPrice, quantity: 1, discountPct: item.discountPct, overridePrice: override }).unitPrice;
  } catch {
    return Number.NaN;
  }
}

export function lineTotalOf(item: Pick<EditorItem, "listPrice" | "discountPct" | "overridePrice" | "quantity">): number {
  return round2(unitPriceOf(item) * (Number.isFinite(item.quantity) ? item.quantity : 0));
}

/** Zbirovi uređivača — isti račun kao na serveru, plus osnovica i PDV iz bruto iznosa. */
export function editorTotals(items: EditorItem[], vatRate: number) {
  const t = orderTotals(items.map((i) => ({ listPrice: i.listPrice, quantity: i.quantity || 0, lineTotal: lineTotalOf(i) })));
  const { net, vat } = vatBreakdown(t.total, vatRate);
  return { ...t, net, vat };
}

/** Stavka iz kataloga: postojeći ključ podiže količinu, novi se dodaje na kraj. */
export function addCatalogItem(
  items: EditorItem[],
  hit: {
    storeId: string; storeName: string; wcProductId: number; wcVariationId: number | null;
    sku: string; name: string; imageUrl: string; listPrice: number | null; stockQuantity?: number | null;
  },
  discountPct: number,
): EditorItem[] {
  const key = editorItemKey(hit);
  if (items.some((i) => editorItemKey(i) === key)) {
    return items.map((i) => (editorItemKey(i) === key ? { ...i, quantity: (i.quantity || 0) + 1 } : i));
  }
  return [
    ...items,
    {
      storeId: hit.storeId, storeName: hit.storeName, wcProductId: hit.wcProductId, wcVariationId: hit.wcVariationId,
      sku: hit.sku, name: hit.name, imageUrl: hit.imageUrl,
      quantity: 1, listPrice: hit.listPrice ?? 0, discountPct, overridePrice: null,
      stockQuantity: hit.stockQuantity ?? null,
    },
  ];
}

/** Stepper u pretrazi: postavlja tačnu količinu artikla u narudžbi. Novi artikal ulazi s
 *  rabatom partnera, postojećem se mijenja samo količina (ručna cijena ostaje), a 0 ga uklanja. */
export function setCatalogItemQuantity(
  items: EditorItem[],
  hit: Parameters<typeof addCatalogItem>[1],
  discountPct: number,
  quantity: number,
): EditorItem[] {
  const key = editorItemKey(hit);
  const n = Math.max(0, Math.round(Number(quantity) || 0));
  if (n === 0) return removeItem(items, key);
  if (items.some((i) => editorItemKey(i) === key)) return patchItem(items, key, { quantity: n });
  return [...items, editorItemFromSnapshot(hit, discountPct, n)];
}

/** Snapshot iz kataloga → stavka korpe. Brzo dodavanje s kartice izloga zaobilazi detalj
 *  artikla, pa je ovo jedino mjesto koje zna kako se snapshot pretvara u red narudžbe. */
export function editorItemFromSnapshot(
  snap: {
    storeId: string; storeName: string; wcProductId: number; wcVariationId: number | null;
    sku: string; name: string; imageUrl: string; listPrice: number | null; stockQuantity?: number | null;
  },
  discountPct: number,
  quantity: number,
): EditorItem {
  return {
    storeId: snap.storeId, storeName: snap.storeName, wcProductId: snap.wcProductId, wcVariationId: snap.wcVariationId,
    sku: snap.sku, name: snap.name, imageUrl: snap.imageUrl,
    quantity, listPrice: snap.listPrice ?? 0, discountPct, overridePrice: null,
    stockQuantity: snap.stockQuantity ?? null,
  };
}

export function patchItem(items: EditorItem[], key: string, patch: Partial<EditorItem>): EditorItem[] {
  return items.map((i) => (editorItemKey(i) === key ? { ...i, ...patch } : i));
}

export function removeItem(items: EditorItem[], key: string): EditorItem[] {
  return items.filter((i) => editorItemKey(i) !== key);
}

/** Stavke narudžbe s API-ja → stavke uređivača. */
export function fromOrderItems(
  orderItems: {
    storeId: string; storeName: string; wcProductId: number; wcVariationId: number | null;
    sku: string; name: string; imageUrl: string; quantity: number; listPrice: number; discountPct: number;
    unitPrice: number; priceOverride: boolean; unavailable?: boolean; stockQuantity?: number | null;
  }[] = [],
): EditorItem[] {
  return orderItems.map((i) => ({
    storeId: i.storeId, storeName: i.storeName, wcProductId: i.wcProductId, wcVariationId: i.wcVariationId,
    sku: i.sku, name: i.name, imageUrl: i.imageUrl, quantity: i.quantity, listPrice: i.listPrice,
    discountPct: i.discountPct, overridePrice: i.priceOverride ? i.unitPrice : null,
    unavailable: i.unavailable, stockQuantity: i.stockQuantity ?? null,
  }));
}

/** Stavke uređivača → tijelo zahtjeva. Količina se zaokružuje na cijeli broj ≥ 1. */
export function toItemInputs(items: EditorItem[]): EditorItemInput[] {
  return items.map((i) => ({
    storeId: i.storeId,
    wcProductId: i.wcProductId,
    wcVariationId: i.wcVariationId,
    quantity: Math.max(1, Math.round(Number(i.quantity) || 1)),
    overridePrice: i.overridePrice,
  }));
}
